import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../context/LanguageContext";

/* ============================
   Popup Modal Component (Zoom + Drag for image)
============================ */
function PopupModal({ content, onClose }) {
  const isImage = content?.type === "img";

  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0 });
  const [initialLoad, setInitialLoad] = useState(true);

  // Calculate initial scale to fit the full image
  useEffect(() => {
    if (isImage && initialLoad) {
      const modalWidth = window.innerWidth * 0.9;
      const modalHeight = window.innerHeight * 0.9;
      const maxWidth = Math.min(450, modalWidth);

      // Give a moment for the image to load, then fit to container
      setTimeout(() => {
        const img = document.querySelector('.popup-image');
        if (img) {
          const scaleX = (maxWidth - 32) / img.naturalWidth;
          const scaleY = (modalHeight - 32) / img.naturalHeight;
          const fitScale = Math.min(scaleX, scaleY, 1);
          setScale(fitScale);
        }
        setInitialLoad(false);
      }, 50);
    }
  }, [isImage, initialLoad]);

  const handleWheel = (e) => {
    if (!isImage) return;
    e.preventDefault();
    let newScale = scale + e.deltaY * -0.001;
    newScale = Math.min(Math.max(0.5, newScale), 4);
    setScale(newScale);
  };

  const handleMouseDown = (e) => {
    if (!isImage) return;
    e.preventDefault();
    setDragging(true);
    setStart({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    e.preventDefault();
    setPos({ x: e.clientX - start.x, y: e.clientY - start.y });
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="p-4 w-[90vw] max-w-[450px] max-h-[90vh] rounded-lg relative overflow-hidden">
        <button
          onClick={onClose}
          className="absolute top-2 right-3 text-red-700 text-2xl z-50"
        >
          <XMarkIcon className="h-10 w-10" />
        </button>

        {/* Content wrapper */}
        <div
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          className={isImage ? "w-full h-full cursor-grab active:cursor-grabbing" : ""}
          style={{
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            userSelect: "none",
          }}
        >
          <div
            style={{
              transform: isImage
                ? `translate(${pos.x}px, ${pos.y}px) scale(${scale})`
                : "none",
              transition: dragging ? "none" : "transform 0.15s ease-out",
            }}
          >
            {content}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================
   Home Page
============================ */
function HomePage() {
  const [hoveredId, setHoveredId] = useState(null);
  const [popup, setPopup] = useState(null);
  const [selectedChar, setSelectedChar] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const detectiveImages = [
    "/shadows/D1.svg",
    "/shadows/D2.svg",
    "/shadows/D3.svg",
    "/shadows/D4.svg",
    "/shadows/D5.svg",
    "/shadows/D6.svg",
  ];

  const characters = [
    { id: "monie", label: "Monie" },
    { id: "wynn", label: "Wynn" },
    { id: "jo", label: "Jozette" },
    { id: "den", label: "Den" },
  ];

  const stationContent = (
    <div className="grid grid-cols-2 gap-4">
      {characters.map((char) => (
        <div key={char.id} className="flex flex-col items-center gap-4">
          <img
            src={`/board/${char.id}.webp`}
            alt={char.label}
            className="w-36 object-contain cursor-pointer hover:scale-105 transition-transform"
            onClick={() => setSelectedChar(char.id)}
          />
        </div>
      ))}
    </div>
  );

  /* ============================
     Detective Image Loop
  ============================ */
  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % detectiveImages.length);
        setFade(true);
      }, 500);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="2xl:overflow-hidden overflow-y-auto relative w-full min-h-screen flex flex-col md:flex-row bg-gradient-to-b from-[#131b33] to-black">

      {/* Background */}
      <div className="md:absolute relative md:inset-0 inset-auto md:w-full">
        <img
          src="/bg/main.webp"
          alt="background"
          className="w-full h-auto object-cover"
        />
      </div>

      {/* Left (60%) */}
      <div className="w-0 md:w-3/5 flex items-center justify-center"></div>

      {/* Right (40%) */}
      <div className="w-full md:w-2/5 2xl:h-full flex items-center justify-center 2xl:mt-10 mt-4">
        <div className="w-full max-w-md">
          <div className="space-y-4 transition-all duration-500 ease-in-out">

            {/* ROW 1 */}
            <div
              className="flex gap-4 transition-all duration-500 ease-in-out"
              style={{
                height:
                  hoveredId === "oal" || hoveredId === "station"
                    ? "11rem"
                    : "10rem",
              }}
            >
              {/* Once A Live */}
              <div
                onClick={() => navigate("/oal")}
                onMouseEnter={() => setHoveredId("oal")}
                onMouseLeave={() => setHoveredId(null)}
                className="flex items-center justify-center transition-transform duration-500 ease-in-out flex-1 cursor-pointer"
                style={{
                  transform:
                    hoveredId === "oal"
                      ? "scale(1.08)"
                      : hoveredId === "station"
                        ? "scale(0.92)"
                        : "scale(1)",
                }}
              >
                <img src="/menus/Menu1.webp" alt="what is once a live" />
              </div>

              {/* Station */}
              <div
                onClick={() => setPopup("station")}
                onMouseEnter={() => setHoveredId("station")}
                onMouseLeave={() => setHoveredId(null)}
                className="flex items-center justify-center transition-transform duration-500 ease-in-out flex-1 cursor-pointer"
                style={{
                  flex:
                    hoveredId === "station"
                      ? 1.4
                      : hoveredId === "oal"
                        ? 0.6
                        : 1,
                  transform:
                    hoveredId === "station"
                      ? "scale(1.08)"
                      : hoveredId === "oal"
                        ? "scale(0.92)"
                        : "scale(1)",
                }}
              >
                <img src="/menus/Menu2.webp" alt="station marmoris" />
              </div>
            </div>

            {/* ROW 2 */}
            <div
              className="pt-6 flex gap-4 transition-all duration-500 ease-in-out"
              style={{ height: hoveredId === "detective" ? "11rem" : "10rem" }}
            >
              <a
                href="https://quiz.oalproject.org/"
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => {
                  setHoveredId("detective");
                  setFade(true);
                }}
                onMouseLeave={() => {
                  setHoveredId(null);
                  setFade(false);
                }}
                className="relative flex items-center justify-center w-full h-full transition-transform duration-500 ease-in-out"
                style={{
                  transform:
                    hoveredId === "detective" ? "scale(1.08)" : "scale(1)",
                }}
              >
                <img
                  src="/menus/Menu3.webp"
                  alt="what type of detective you are"
                  className="h-fit w-fit"
                />

                <img
                  src={detectiveImages[currentIndex]}
                  alt="detective overlay"
                  className={`absolute -top-1 left-[47%] rotate-[8.06deg] h-full w-[60%] object-contain pointer-events-none transition-opacity duration-500`}
                  style={{ opacity: fade ? 1 : 0 }}
                />
              </a>
            </div>

            {/* ROW 3 */}
            <div
              className="grid grid-cols-2 gap-1 transition-all duration-500 ease-in-out"
              style={{
                height:
                  hoveredId === "shop" ||
                    hoveredId === "showcase" ||
                    hoveredId === "lib"
                    ? "12rem"
                    : "11rem",
              }}
            >
              {/* Shop */}
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSfoYFgaVTJB0PwtO3YX2HwKD7g_mZ8KGO4PhzzZ1DidebmhZg/viewform"
                onMouseEnter={() => setHoveredId("shop")}
                onMouseLeave={() => setHoveredId(null)}
                className="flex items-center justify-center transition-transform duration-500 ease-in-out cursor-pointer"
                style={{
                  gridRow: "span 2",
                  transform:
                    hoveredId === "shop"
                      ? "scale(1.08)"
                      : hoveredId === "showcase" || hoveredId === "lib"
                        ? "scale(0.95)"
                        : "scale(1)",
                }}
              >
                <img src="/menus/Menu4.webp" alt="shop" />
              </a>

              {/* Showcase */}
              <div className="flex flex-col gap-0 transition-all duration-500 ease-in-out -ml-4">
                <div
                  onClick={() => navigate("/showcase")}
                  onMouseEnter={() => setHoveredId("showcase")}
                  onMouseLeave={() => setHoveredId(null)}
                  className="items-center justify-center transition-transform duration-500 ease-in-out cursor-pointer"
                  style={{
                    transform:
                      hoveredId === "showcase"
                        ? "scale(1.08)"
                        : hoveredId === "shop"
                          ? "scale(0.95)"
                          : "scale(1)",
                  }}
                >
                  <img src="/menus/Menu5.webp" alt="showcase" />
                </div>

                {/* Library */}
                <Link
                  to="/library"
                  onMouseEnter={() => setHoveredId("lib")}
                  onMouseLeave={() => setHoveredId(null)}
                  className="flex items-center justify-center transition-all duration-500 ease-in-out -mt-6"
                  style={{
                    transform:
                      hoveredId === "lib"
                        ? "scale(1.08)"
                        : hoveredId === "shop"
                          ? "scale(0.95)"
                          : "scale(1)",
                  }}
                >
                  <img src="/menus/Menu6.webp" alt="library" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================
          POPUP MODALS
      ============================ */}

      {/* Station popup */}
      {popup === "station" && !selectedChar && (
        <PopupModal content={stationContent} onClose={() => setPopup(null)} />
      )}

      {/* Character popup (zoom enabled) */}
      {selectedChar && (
        <PopupModal
          content={
            <img
              src={`/v/${selectedChar}_${lang == "TH" ? "thai" : "eng"}.svg`}
              alt={selectedChar}
            />
          }
          onClose={() => setSelectedChar(null)}
        />
      )}
    </div>
  );
}

export default HomePage;
